import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import WebsocketClient from "@ekonsilio/websocket-client";
import propTypes from "prop-types";

import { useWebmessenger } from "./webmessenger";

const WebSocketContext = createContext();

function WebSocketProvider({ children }) {
    const websocket = useRef(null);
    const [connected, setConnected] = useState(false);
    const { token, conversation } = useWebmessenger();

    useEffect(() => {
        if (process.env.REACT_APP_WEBSOCKET && conversation !== null) {
            websocket.current = new WebsocketClient(
                process.env.REACT_APP_WEBSOCKET
            );

            websocket.current.on("open", () => {
                setConnected(true);
            });

            websocket.current.on("close", () => {
                setConnected(false);
            });
        }

        return () => {
            if (websocket && websocket.current) {
                websocket.current.close();
                websocket.current = null;
            }
        };
    }, [conversation]);

    useEffect(() => {
        if (websocket.current && connected && token) {
            websocket.current.emit("auth", {
                token,
            });
        }
    }, [connected, token]);

    const value = useMemo(
        () => ({ websocket: websocket.current, connected }),
        [websocket.current, connected]
    );

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
}

function useWebSocket() {
    const context = useContext(WebSocketContext);

    if (context === undefined) {
        throw new Error("useWebSocket must be used within a WebSocketProvider");
    }

    return context;
}

WebSocketProvider.propTypes = {
    uri: propTypes.string,
    children: propTypes.element.isRequired,
};

WebSocketProvider.defaultProps = {
    uri: "",
};

export { WebSocketProvider, useWebSocket };
